import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CloseIconButton,
  Stack,
} from '@packages/shared';
import { Mail32 } from '@packages/themes/icons';
import type { GTMEventGlycerinBaseClick, GTMEventGlycerinDisplayOverlay } from '@packages/tracking';
import { useTracking } from '@packages/tracking';
import { useState } from 'react';

const transitionDurationOpening = '1.2s';
const transitionDurationClosing = '1.5s';

export type SheetProps = {
  /** Title that opens the sheet */
  title: string;
  /** Content in the opened sheet */
  children: JSX.Element;
  /** Function that is called when the user clicks the X Button */
  onClickClose: () => void;
};

/**
 * A small sheet on the bottom screen that opens when you click on it
 */
export const Sheet = ({ title, children, onClickClose }: SheetProps) => {
  const [open, setOpen] = useState(false);
  const dispatchGTMEvent = useTracking();

  dispatchGTMEvent<GTMEventGlycerinDisplayOverlay>({
    event: 'DisplayOverlay',
    DisplayOverlayData: {
      category: 'newsletter-sheet',
      label: 'show',
    },
  });

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        overflow: 'hidden',
        width: '100%',
        zIndex: 'drawer',
      }}
    >
      {/* Curtain */}
      <Box
        sx={{
          position: 'fixed',
          inset: '0 0 0 0',
          margin: 0,
          padding: 0,
          backgroundColor: 'common.black',
          opacity: 0,
          pointerEvents: 'none',
          transition: `opacity ${transitionDurationClosing} ease-out`,
          ...(open && {
            transition: `opacity ${transitionDurationOpening} ease-in`,
            pointerEvents: 'all',
            opacity: 0.6,
          }),
        }}
        data-testid="newslettersheet-curtain"
        onClick={() => setOpen(false)}
      />

      <Accordion
        expanded={open}
        onChange={(_e, isExpanded) => setOpen(isExpanded)}
        sx={{
          backgroundColor: 'transparent',
          '.MuiAccordionSummary-content': { margin: 0 },
        }}
        disableGutters
      >
        {/* Sheet Header */}
        <AccordionSummary
          expandIcon={null}
          sx={{
            borderBottom: '0.125rem solid',
            borderColor: { xs: 'grey.main', md: 'primary.main' },
            minHeight: 'unset', // Mui's minHeight is really not useful here
            padding: 0, // Mui's padding is really not useful here
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing="1rem"
            sx={{
              margin: 'auto',
              cursor: 'pointer',
              width: { xs: 1, md: '27rem' },
              color: { xs: 'text.dark', md: 'primary.contrastText' },
              backgroundColor: { xs: 'grey.light', md: 'primary.main' },
              borderColor: { xs: 'primary.main', md: 'transparent' },
              borderTop: '0.125rem solid',
              paddingY: 0,
              paddingRight: 1.25,
              paddingLeft: 2.126,
            }}
          >
            <Mail32 />
            <Box sx={{ width: 1, textAlign: 'left', paddingY: 1 }}>{title}</Box>
            <CloseIconButton
              sx={{ color: { xs: 'text.dark', md: 'primary.contrastText' } }}
              onClick={(e) => {
                onClickClose();
                dispatchGTMEvent<GTMEventGlycerinBaseClick>({
                  event: 'BaseClick',
                  BaseClickData: {
                    category: 'newsletter-sheet',
                    label: 'canceled',
                  },
                });
                e.stopPropagation();
              }}
            />
          </Stack>
        </AccordionSummary>

        {/* Content */}
        <AccordionDetails
          sx={{
            backgroundColor: 'grey.light',
            padding: 0,
          }}
        >
          {children}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
